import React from 'react';

import {
  IApplicationJourneyStepDefinition,
  ApplicationJourneyStepKey,
} from './journeyConfig';

interface ApplicationJourneyStepperProps {
  steps: IApplicationJourneyStepDefinition[];
  activeKey: ApplicationJourneyStepKey;
  onStepClick: (step: IApplicationJourneyStepDefinition) => void;
  completion: number;
  variant?: 'grid' | 'stack';
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

const ApplicationJourneyStepper: React.FC<ApplicationJourneyStepperProps> = ({
  steps,
  activeKey,
  onStepClick,
  completion,
  variant = 'grid',
}) => {
  const activeIndex = Math.max(
    0,
    steps.findIndex((step) => step.key === activeKey),
  );

  return (
    <nav
      className={cx('application-stepper', variant === 'stack' && 'is-stack')}
      aria-label="Application progress"
    >
      <ol className="application-stepper__list">
        {steps.map((step, index) => {
          const status =
            index < activeIndex
              ? 'complete'
              : index === activeIndex
              ? 'current'
              : 'upcoming';
          const isComplete = status === 'complete';
          const isCurrent = status === 'current';

          return (
            <li
              key={step.key}
              className={cx(
                'application-stepper__item',
                `is-${status}`,
                index === 0 && 'is-first',
                index === steps.length - 1 && 'is-last',
              )}
            >
              <button
                type="button"
                onClick={() => onStepClick(step)}
                className="application-stepper__trigger"
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className="application-stepper__badge">
                  {isComplete ? (
                    <svg
                      viewBox="0 0 16 16"
                      role="presentation"
                      aria-hidden="true"
                    >
                      <path
                        d="M6.6 11.2 3.4 8l1.2-1.2L6.6 9 11.4 4.2 12.6 5.4z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </span>
                <span className="application-stepper__copy">
                  <span className="application-stepper__title">
                    {step.title}
                  </span>
                  <span className="application-stepper__subtitle">
                    {step.subtitle}
                  </span>
                </span>
              </button>
              {variant === 'stack' && isCurrent ? (
                <div className="application-stepper__substeps-shell">
                  <div
                    className="application-stepper__substeps-host"
                    data-money-substep-nav
                  />
                  <p className="application-stepper__substeps-empty">
                    No sub-steps in this section.
                  </p>
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="application-stepper__progress" aria-hidden="true">
        <div
          className="application-stepper__progress-indicator"
          style={{width: `${completion}%`}}
        />
      </div>
    </nav>
  );
};

export default ApplicationJourneyStepper;
